"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Point = void 0;
const simple_features_js_1 = require("@ngageoint/simple-features-js");
const GridUtils_1 = require("../GridUtils");
const Unit_1 = require("./Unit");
/**
 * Point
 */
class Point extends simple_features_js_1.Point {
    constructor() {
        super(...arguments);
        /**
         * Unit
         */
        this.unit = Unit_1.Unit.DEGREE;
    }
    /**
     * Create a point
     *
     * @param longitude
     *            longitude
     * @param latitude
     *            latitude
     * @param unit
     *            unit; if null, it will use the default degree unit
     * @return point
     */
    static point(longitude, latitude, unit) {
        let point;
        if (unit !== undefined) {
            point = new Point(longitude, latitude);
            point.unit = unit;
        }
        else {
            point = this.degrees(longitude, latitude);
        }
        return point;
    }
    /**
     * Create a point in degrees
     *
     * @param longitude
     *            longitude in degrees
     * @param latitude
     *            latitude in degrees
     * @return point in degrees
     */
    static degrees(longitude, latitude) {
        return Point.point(longitude, latitude, Unit_1.Unit.DEGREE);
    }
    /**
     * Create a point in meters
     *
     * @param longitude
     *            longitude in meters
     * @param latitude
     *            latitude in meters
     * @return point in meters
     */
    static meters(longitude, latitude) {
        return Point.point(longitude, latitude, Unit_1.Unit.METER);
    }
    /**
     * Create a point from a coordinate in a unit to another unit
     *
     * @param fromUnit
     *            unit of provided coordinate
     * @param longitude
     *            longitude
     * @param latitude
     *            latitude
     * @param toUnit
     *            desired unit
     * @return point in unit
     */
    static toUnit(fromUnit, longitude, latitude, toUnit) {
        return GridUtils_1.GridUtils.toUnit(fromUnit, longitude, latitude, toUnit);
    }
    /**
     * Create a point from a coordinate in an opposite unit to another unit
     *
     * @param longitude
     *            longitude
     * @param latitude
     *            latitude
     * @param unit
     *            desired unit
     * @return point in unit
     */
    static toUnitInverse(longitude, latitude, unit) {
        return GridUtils_1.GridUtils.toUnitOpposite(longitude, latitude, unit);
    }
    /**
     * Create a point converting the degrees coordinate to meters
     *
     * @param longitude
     *            longitude in degrees
     * @param latitude
     *            latitude in degrees
     * @return point in meters
     */
    static degreesToMeters(longitude, latitude) {
        return Point.toUnit(Unit_1.Unit.DEGREE, longitude, latitude, Unit_1.Unit.METER);
    }
    /**
     * Create a point converting the meters coordinate to degrees
     *
     * @param longitude
     *            longitude in meters
     * @param latitude
     *            latitude in meters
     * @return point in degrees
     */
    static metersToDegrees(longitude, latitude) {
        return Point.toUnit(Unit_1.Unit.METER, longitude, latitude, Unit_1.Unit.DEGREE);
    }
    /**
     * Create a point
     *
     * @param point
     *            point to copy
     * @param unit
     *            unit
     * @return point
     */
    static pointFromPoint(point, unit) {
        const newPoint = new Point(point);
        newPoint.unit = unit;
        return newPoint;
    }
    /**
     * Get the longitude
     *
     * @return longitude
     */
    getLongitude() {
        return this.x;
    }
    /**
     * Set the longitude
     *
     * @param longitude
     *            longitude
     */
    setLongitude(longitude) {
        this.x = longitude;
    }
    /**
     * Get the latitude
     *
     * @return latitude
     */
    getLatitude() {
        return this.y;
    }
    /**
     * Set the latitude
     *
     * @param latitude
     *            latitude
     */
    setLatitude(latitude) {
        this.y = latitude;
    }
    /**
     * Get the unit
     *
     * @return unit
     */
    getUnit() {
        return this.unit;
    }
    /**
     * Set the unit
     *
     * @param unit
     *            unit
     */
    setUnit(unit) {
        this.unit = unit;
    }
    /**
     * Is in the provided unit type
     *
     * @param unit
     *            unit
     * @return true if in the unit
     */
    isUnit(unit) {
        return this.unit === unit;
    }
    /**
     * Is this point in degrees
     *
     * @return true if degrees
     */
    isDegrees() {
        return this.isUnit(Unit_1.Unit.DEGREE);
    }
    /**
     * Is this point in meters
     *
     * @return true if meters
     */
    isMeters() {
        return this.isUnit(Unit_1.Unit.METER);
    }
    /**
     * Convert to the unit
     *
     * @param unit
     *            unit
     * @return point in units, same point if equal units
     */
    toUnit(unit) {
        let newPoint;
        if (this.isUnit(unit)) {
            newPoint = this;
        }
        else {
            newPoint = GridUtils_1.GridUtils.toUnit(this.unit, this.getLongitude(), this.getLatitude(), unit);
        }
        return newPoint;
    }
    /**
     * Convert to degrees
     *
     * @return point in degrees, same point if already in degrees
     */
    toDegrees() {
        return this.toUnit(Unit_1.Unit.DEGREE);
    }
    /**
     * Convert to meters
     *
     * @return point in meters, same point if already in meters
     */
    toMeters() {
        return this.toUnit(Unit_1.Unit.METER);
    }
    /**
     * Get the pixel where the point fits into tile
     *
     * @param tile
     *            tile
     * @return pixel
     */
    getPixelFromTile(tile) {
        return this.getPixel(tile.getWidth(), tile.getHeight(), tile.getBounds());
    }
    /**
     * Get the pixel where the point fits into the bounds
     *
     * @param width
     *            width
     * @param height
     *            height
     * @param bounds
     *            bounds
     * @return pixel
     */
    getPixel(width, height, bounds) {
        return GridUtils_1.GridUtils.getPixel(width, height, bounds, this);
    }
    /**
     * Copy the point
     *
     * @return point copy
     */
    copy() {
        const copy = new Point(this);
        copy.unit = this.unit;
        return copy;
    }
    /**
     * {@inheritDoc}
     */
    equals(other) {
        if (this === other)
            return true;
        if (!super.equals(other))
            return false;
        if (!(other instanceof Point))
            return false;
        if (this.unit !== other.getUnit())
            return false;
        return true;
    }
}
exports.Point = Point;
//# sourceMappingURL=Point.js.map